import baseApi from "../api/baseApi";

const staffTableTags = ["StaffWithRoles", "StaffPendingRoles"];
const rolesListTags = ["RolesList"];

const invalidateStaffTableCache = async (
  _arg,
  { dispatch, queryFulfilled },
) => {
  try {
    await queryFulfilled;
    dispatch(baseApi.util.invalidateTags(staffTableTags));
  } catch {
    // Keep cached table data when the mutation fails.
  }
};

const invalidateRolesListCache = async (_arg, { dispatch, queryFulfilled }) => {
  try {
    await queryFulfilled;
    dispatch(baseApi.util.invalidateTags(rolesListTags));
  } catch {
    // Keep cached role list when the mutation fails.
  }
};

const userApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllInstructors: builder.query({
      async queryFn(
        { search = "", status = "", page = 1, limit = 10 } = {},
        _queryApi,
        _extraOptions,
        fetchWithBQ,
      ) {
        const params = { page, limit };

        if (search) {
          params.search = search;
        }

        if (status) {
          params.status = status;
        }

        const result = await fetchWithBQ({
          url: "/user/getAllInstructors",
          method: "GET",
          credentials: "include",
          params,
        });

        if (result.error) {
          if (result.error.status === 404) {
            return {
              data: {
                success: false,
                message: result.error.data?.message || "No instructors found",
                data: [],
                pagination: {
                  currentPage: page,
                  totalPages: 1,
                  totalInstructors: 0,
                  limit,
                },
              },
            };
          }

          return { error: result.error };
        }

        return { data: result.data };
      },
      providesTags: [{ type: "Instructors", id: "LIST" }],
    }),
    getAllStudents: builder.query({
      async queryFn(
        { search = "", status = "", page = 1, limit = 10 } = {},
        _queryApi,
        _extraOptions,
        fetchWithBQ,
      ) {
        const params = { page, limit };

        if (search) {
          params.search = search;
        }

        if (status) {
          params.status = status;
        }

        const result = await fetchWithBQ({
          url: "/user/getAllStudents",
          method: "GET",
          credentials: "include",
          params,
        });

        if (result.error) {
          if (result.error.status === 404) {
            return {
              data: {
                success: false,
                message: result.error.data?.message || "No students found",
                data: [],
                pagination: {
                  currentPage: page,
                  totalPages: 1,
                  totalStudents: 0,
                  limit,
                },
              },
            };
          }

          return { error: result.error };
        }

        const students = Array.isArray(result.data?.data)
          ? result.data.data
          : [];

        return {
          data: {
            ...result.data,
            data: students,
          },
        };
      },
      providesTags: [{ type: "Students", id: "LIST" }],
    }),
    // Staff with assigned roles for Access Role table — GET /staff/getStaffWithRoles
    getAllRoles: builder.query({
      async queryFn(
        { role = "", status = "", page = 1, limit = 10 } = {},
        _queryApi,
        _extraOptions,
        fetchWithBQ,
      ) {
        const params = { page, limit };

        if (role) {
          params.role = role;
        }

        if (status) {
          params.status = status;
        }

        const result = await fetchWithBQ({
          url: "/staff/getStaffWithRoles",
          method: "GET",
          credentials: "include",
          params,
        });

        if (result.error) {
          if (result.error.status === 404) {
            return {
              data: {
                success: false,
                message:
                  result.error.data?.message || "No staff with roles found",
                data: [],
                pagination: {
                  currentPage: page,
                  totalPages: 1,
                  totalStaff: 0,
                  limit,
                },
              },
            };
          }

          return { error: result.error };
        }

        const staff = Array.isArray(result.data?.data) ? result.data.data : [];

        return {
          data: {
            ...result.data,
            data: staff,
          },
        };
      },
      providesTags: (result) =>
        result?.data ? [{ type: "StaffWithRoles", id: "LIST" }] : [],
    }),
    getStaffWithPendingStatus: builder.query({
      async queryFn(
        { role = "", page = 1, limit = 10 } = {},
        _queryApi,
        _extraOptions,
        fetchWithBQ,
      ) {
        const params = { page, limit };

        if (role) {
          params.role = role;
        }

        const result = await fetchWithBQ({
          url: "/staff/getStaffWithPendingStatus",
          method: "GET",
          credentials: "include",
          params,
        });

        if (result.error) {
          if (result.error.status === 404) {
            return {
              data: {
                success: false,
                message:
                  result.error.data?.message ||
                  "No staff with pending status found",
                data: [],
                pagination: {
                  currentPage: page,
                  totalPages: 1,
                  totalStaff: 0,
                  limit,
                },
              },
            };
          }

          return { error: result.error };
        }

        const staff = Array.isArray(result.data?.data) ? result.data.data : [];

        return {
          data: {
            ...result.data,
            data: staff,
          },
        };
      },
      providesTags: (result) =>
        result?.data ? [{ type: "StaffPendingRoles", id: "LIST" }] : [],
    }),
    getStaffWithNoRoles: builder.query({
      query: ({ search = "", status = "" } = {}) => {
        const params = {};

        if (search) {
          params.search = search;
        }

        if (status) {
          params.status = status;
        }

        return {
          url: "/staff/getStaffWithNoRoles",
          method: "GET",
          credentials: "include",
          params,
        };
      },
    }),
    registerStaff: builder.mutation({
      query: (payload) => ({
        url: "/staff/register",
        method: "POST",
        credentials: "include",
        body: payload,
      }),
    }),
    createRole: builder.mutation({
      query: (payload) => ({
        url: "/role/createRole",
        method: "POST",
        credentials: "include",
        body: payload,
      }),
      invalidatesTags: rolesListTags,
      onQueryStarted: invalidateRolesListCache,
    }),
    deleteRole: builder.mutation({
      query: (payload) => {
        const roleId = String(payload.roleId || payload.id || "");

        return {
          url: "/role/deleteRole",
          method: "PATCH",
          credentials: "include",
          body: {
            ...payload,
            roleId,
            id: roleId,
          },
        };
      },
      invalidatesTags: [...rolesListTags, ...staffTableTags],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            baseApi.util.invalidateTags([...rolesListTags, ...staffTableTags]),
          );
        } catch {
          // Keep cached data when the mutation fails.
        }
      },
    }),
    updateRole: builder.mutation({
      query: (payload) => {
        const roleId = String(payload.roleId || payload.id || "");

        return {
          url: "/role/updateRole",
          method: "PATCH",
          credentials: "include",
          body: {
            ...payload,
            roleId,
            id: roleId,
          },
        };
      },
      invalidatesTags: rolesListTags,
      onQueryStarted: invalidateRolesListCache,
    }),
    assignRole: builder.mutation({
      query: (payload) => ({
        url: "/staff/assignRole",
        method: "POST",
        credentials: "include",
        body: payload,
      }),
      invalidatesTags: staffTableTags,
      onQueryStarted: invalidateStaffTableCache,
    }),
    updateStaffRole: builder.mutation({
      query: (payload) => ({
        url: "/staff/updateStaffRole",
        method: "PATCH",
        credentials: "include",
        body: payload,
      }),
      invalidatesTags: staffTableTags,
      onQueryStarted: invalidateStaffTableCache,
    }),
    toggleStaffStatus: builder.mutation({
      query: (payload) => ({
        url: "/staff/toggleStaffStatus",
        method: "PATCH",
        credentials: "include",
        body: payload,
      }),
      invalidatesTags: staffTableTags,
      onQueryStarted: invalidateStaffTableCache,
    }),
    removeStaffWithPendingStatus: builder.mutation({
      query: (payload) => ({
        url: "/staff/removeStaffWithPendingStatus",
        method: "PATCH",
        credentials: "include",
        body: payload,
      }),
      invalidatesTags: staffTableTags,
      onQueryStarted: invalidateStaffTableCache,
    }),
    restoreStaffWithPendingStatus: builder.mutation({
      query: ({ email, staffId }) => ({
        url: "/staff/restoreStaffWithPendingStatus",
        method: "PATCH",
        credentials: "include",
        body: { email, staffId },
      }),
      invalidatesTags: staffTableTags,
      onQueryStarted: invalidateStaffTableCache,
    }),
    deleteStaffPermanently: builder.mutation({
      query: (staffId) => ({
        url: `/staff/deleteStaffPermanently/${staffId}`,
        method: "DELETE",
        credentials: "include",
      }),
      invalidatesTags: staffTableTags,
      onQueryStarted: invalidateStaffTableCache,
    }),

    // Role definitions for select dropdowns — GET /role/getAllRoles
    getRolesList: builder.query({
      query: () => ({
        url: "/role/getAllRoles",
        method: "GET",
        credentials: "include",
      }),
      extraOptions: { skipAuthRedirect: true },
      transformResponse: (response) => ({
        ...response,
        data: Array.isArray(response?.data) ? response.data : [],
      }),
      providesTags: [{ type: "RolesList", id: "LIST" }],
    }),

    getInstructorProfile: builder.query({
      query: (instructorId) => ({
        url: `/instructor/${instructorId}/details/all`,
        method: "GET",
        credentials: "include",
      }),
      providesTags: (_result, _error, instructorId) => [
        { type: "Instructors", id: instructorId },
      ],
    }),

    changeStudentStatus: builder.mutation({
      query: ({ studentId, accountStatus }) => ({
        url: "user/changeStudentStatus",
        method: "PATCH",
        credentials: "include",
        body: {
          studentId,
          accountStatus,
        },
      }),
      invalidatesTags: [{ type: "Students", id: "LIST" }],
    }),
  }),
});

export const {
  useGetAllInstructorsQuery,
  useLazyGetAllInstructorsQuery,
  useGetAllStudentsQuery,
  useLazyGetAllStudentsQuery,
  useGetAllRolesQuery,
  useLazyGetAllRolesQuery,
  useGetStaffWithPendingStatusQuery,
  useLazyGetStaffWithPendingStatusQuery,
  useGetStaffWithNoRolesQuery,
  useLazyGetStaffWithNoRolesQuery,
  useRegisterStaffMutation,
  useCreateRoleMutation,
  useUpdateRoleMutation,
  useDeleteRoleMutation,
  useAssignRoleMutation,
  useUpdateStaffRoleMutation,
  useToggleStaffStatusMutation,
  useRemoveStaffWithPendingStatusMutation,
  useRestoreStaffWithPendingStatusMutation,
  useDeleteStaffPermanentlyMutation,
  useGetRolesListQuery,
  useLazyGetRolesListQuery,
  useGetInstructorProfileQuery,
  useChangeStudentStatusMutation,
} = userApi;
